import { PARTNER_ORGANIZATION, PRODUCT_CATEGORY } from '@schuettflix/interfaces';
import type { LineItem } from '@schuettflix/summary';
import { getProductCategoryByProductType } from '@schuettflix/product-group-utils';

type RevenueType = 'REVENUE' | 'EXPENSE';

export type FinanceSettingsOrganization = {
    organizationId: PARTNER_ORGANIZATION;
    lineItems: LineItem[];
    totalAmountNet: number;
    includedProductCategories: PRODUCT_CATEGORY[];
    financeSettings: {
        revenueType: RevenueType;
        debtorOrganizationId: number;
        creditorOrganizationId: number;
        lineItems: LineItem[];
    }[];
};

type LineItemAggregate = {
    organizationId: PARTNER_ORGANIZATION;
    lineItems: LineItem[];
    totalAmountNet: number;
    includedProductCategories: PRODUCT_CATEGORY[];
    revenueType: RevenueType;
    debtorOrganizationId: PARTNER_ORGANIZATION;
    creditorOrganizationId: PARTNER_ORGANIZATION;
};

type GroupLineItemsByOrganizationParams = {
    lineItems: LineItem[];
    platformOrganizationId: number;
    customerOrganizationId: number;
};

export function groupLineItemsByOrganization({
    lineItems,
    platformOrganizationId,
    customerOrganizationId,
}: GroupLineItemsByOrganizationParams): FinanceSettingsOrganization[] {
    const aggregates = buildLineItemAggregates(lineItems, platformOrganizationId);
    const organizations = mergeIntoOrganizations(aggregates);

    return organizations.sort((a, b) => {
        if (a.organizationId === customerOrganizationId) return -1;
        if (b.organizationId === customerOrganizationId) return 1;
        return 0;
    });
}

function buildLineItemAggregates(lineItems: LineItem[], platformOrganizationId: number): LineItemAggregate[] {
    return lineItems
        .reduce<Map<string, LineItemAggregate>>((acc, lineItem) => {
            const key = `${lineItem.debtorOrganizationId}-${lineItem.creditorOrganizationId}`;
            const revenueType: RevenueType =
                lineItem.creditorOrganizationId === platformOrganizationId ? 'REVENUE' : 'EXPENSE';
            const organizationId =
                revenueType === 'REVENUE' ? lineItem.debtorOrganizationId : lineItem.creditorOrganizationId;

            const existing = acc.get(key);
            const productCategory = getProductCategoryByProductType(lineItem.productType);

            if (existing) {
                acc.set(key, {
                    ...existing,
                    lineItems: [...existing.lineItems, lineItem],
                    totalAmountNet: existing.totalAmountNet + lineItem.amountNet,
                    includedProductCategories: [...new Set([...existing.includedProductCategories, productCategory])],
                });
            } else {
                acc.set(key, {
                    organizationId,
                    lineItems: [lineItem],
                    totalAmountNet: lineItem.amountNet,
                    revenueType,
                    includedProductCategories: [productCategory],
                    debtorOrganizationId: lineItem.debtorOrganizationId,
                    creditorOrganizationId: lineItem.creditorOrganizationId,
                });
            }

            return acc;
        }, new Map())
        .values()
        .toArray();
}

function signedAmount(aggregate: LineItemAggregate): number {
    return aggregate.revenueType === 'REVENUE' ? aggregate.totalAmountNet : -aggregate.totalAmountNet;
}

function mergeIntoOrganizations(aggregates: LineItemAggregate[]): FinanceSettingsOrganization[] {
    return aggregates
        .reduce<Map<PARTNER_ORGANIZATION, FinanceSettingsOrganization>>((acc, aggregate) => {
            const { organizationId } = aggregate;

            const existing = acc.get(organizationId);
            const billingEntry =
                aggregate.debtorOrganizationId !== 'TRANSPORT_EXCHANGE' &&
                aggregate.creditorOrganizationId !== 'TRANSPORT_EXCHANGE'
                    ? [
                          {
                              revenueType: aggregate.revenueType,
                              debtorOrganizationId: aggregate.debtorOrganizationId,
                              creditorOrganizationId: aggregate.creditorOrganizationId,
                              lineItems: aggregate.lineItems,
                          },
                      ]
                    : [];

            if (existing) {
                acc.set(organizationId, {
                    ...existing,
                    lineItems: [...existing.lineItems, ...aggregate.lineItems],
                    totalAmountNet: existing.totalAmountNet + signedAmount(aggregate),
                    includedProductCategories: [
                        ...new Set([...existing.includedProductCategories, ...aggregate.includedProductCategories]),
                    ],
                    financeSettings: [...existing.financeSettings, ...billingEntry],
                });
            } else {
                acc.set(organizationId, {
                    organizationId,
                    lineItems: aggregate.lineItems,
                    totalAmountNet: signedAmount(aggregate),
                    includedProductCategories: aggregate.includedProductCategories,
                    financeSettings: billingEntry,
                });
            }

            return acc;
        }, new Map())
        .values()
        .toArray();
}
